import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SIEM_GROUPS_KEY, setCompanySiemGroup } from '../../api/securiteeSiemGroups'
import { companySettingsQuery, type CompanySiemCard } from '../../api/settingsCustomers'

/**
 * Grupul SIEM al unei firme existente, in fila ei Settings. Mutarea trece prin
 * acelasi capat ca ecranul SIEM groups (drepturi si istoric acolo).
 */
export function CompanySiemGroupCard({ companyId, companyName, card }: { companyId: number; companyName: string; card: CompanySiemCard }) {
  const client = useQueryClient()
  const [target, setTarget] = useState(card.group === null ? '' : String(card.group.ID))
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const targetName = card.groups.find((g) => String(g.ID) === target)?.name ?? ''

  const move = async () => {
    setBusy(true)
    setError('')
    try {
      await setCompanySiemGroup(companyId, Number(target))
      setConfirm(false)
      await Promise.all([
        client.invalidateQueries({ queryKey: companySettingsQuery(companyId).queryKey }),
        client.invalidateQueries({ queryKey: SIEM_GROUPS_KEY }),
      ])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="portlet light bg-inverse bordered-left border-mauve company-siem-group" aria-label="SIEM group">
      <div className="portlet-title">
        <div className="caption color-mauve">
          <i className="fas fa-satellite-dish" aria-hidden="true"></i> <span className="caption-subject bold uppercase">SIEM group</span>
        </div>
      </div>
      <div className="portlet-body" style={{ fontSize: 13 }}>
        {card.group === null ? (
          <div className="alert alert-info" style={{ marginBottom: 10 }}>
            This company has no SIEM group. OptimumDesk detects on its events itself, so XDR works without one. A group is
            only needed if you switch the company to Wazuh SIEM in Settings &gt; Securitee &gt; XDR.
          </div>
        ) : (
          <p style={{ marginBottom: 10 }}>
            Its events can be forwarded to the Wazuh of <strong>{card.group.name}</strong>
            {card.inherited && card.parent !== null ? <>, because it follows its parent company, {card.parent}.</> : '.'}
          </p>
        )}

        {card.canChange ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <select
              className="form-control input-sm"
              aria-label={`SIEM group of ${companyName}`}
              value={target}
              disabled={busy}
              onChange={(e) => {
                setTarget(e.target.value)
                setConfirm(false)
              }}
              style={{ width: 'auto', maxWidth: '100%' }}
            >
              <option value="">Choose a SIEM group…</option>
              {card.groups.map((g) => (
                <option key={g.ID} value={g.ID}>
                  {g.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-default btn-sm"
              aria-label="Change SIEM group"
              disabled={busy || target === '' || target === String(card.group?.ID ?? '')}
              onClick={() => setConfirm(true)}
            >
              Change
            </button>
          </div>
        ) : (
          card.parent === null && <p className="text-muted" style={{ margin: 0 }}>Only a platform administrator can move a company to another SIEM group.</p>
        )}

        {confirm && (
          <div className="alert alert-danger" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 10, marginBottom: 0 }}>
            <span>
              {card.group === null
                ? `The events of ${companyName} and its subsidiaries start going to ${targetName}, and XDR can be turned on.`
                : `From now on the events of ${companyName} and its subsidiaries go to ${targetName}. What was already sent stays in ${card.group.name}.`}
            </span>
            <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => void move()}>
              {card.group === null ? 'Yes, put it there' : 'Yes, move it'}
            </button>
            <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setConfirm(false)}>
              Cancel
            </button>
          </div>
        )}
        {error && (
          <div className="alert alert-danger" style={{ marginTop: 10, marginBottom: 0 }}>
            {error}
          </div>
        )}
      </div>
    </section>
  )
}
